import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {AngularFireAuth} from '@angular/fire/compat/auth';
import {BehaviorSubject, Observable} from 'rxjs';
import {environment} from '../../../environments/environment';
import {CookieService} from 'ngx-cookie-service';
import {Q9IntercomService} from '../services/q9-intercom.service';
import {take} from 'rxjs/operators';
import {SessionService, UserMACR, MapStates, SentryService} from '@q9elements/ui-kit/common';

interface PermissionSet {
  access: string;
  state: string;
  macr?: UserMACR;
  canCopy?: string | boolean;
  canAddNotes?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private userSubject: BehaviorSubject<any> = new BehaviorSubject(null);
  public user$: Observable<any> = this.userSubject.asObservable();
  private authToken: string;
  private macr: UserMACR;
  private canEdit: boolean;
  private canCopyMap: boolean;
  private canAddNotes: boolean;
  private mapState: string;

  constructor(
    private http: HttpClient,
    private cookieSvc: CookieService,
    private intercom: Q9IntercomService,
    private angularFireAuth: AngularFireAuth,
    private sessionService: SessionService,
    private sentryService: SentryService
  ) {}

  public setDiagramAccess(permission?: PermissionSet): boolean {
    this.canEdit = !(
      !permission ||
      permission.access.toUpperCase() === 'NONE' ||
      permission.access.toUpperCase() === 'VIEW' ||
      permission.state === MapStates.DELETED
    );

    if (typeof permission.canCopy !== typeof void 0) {
      this.canCopyMap = Boolean(permission.canCopy);
    }

    if (typeof permission.macr !== typeof void 0) {
      this.macr = permission.macr;
    }

    if (typeof permission.canAddNotes !== typeof void 0) {
      this.canAddNotes = permission.state !== MapStates.DELETED && Boolean(permission.canAddNotes);
    }

    this.mapState = permission.state.toUpperCase();

    return this.canEdit;
  }

  public getMACR() {
    return this.macr;
  }

  public getCanCopyMap() {
    return this.canCopyMap;
  }

  public canEditDiagram() {
    return this.canEdit;
  }

  /**
   * Returns true if user can add notes to the diagram in a view mode.
   */
  public getCanAddNotes() {
    return this.canAddNotes;
  }

  public checkDiagramPermission(diagramId: string, state: string) {
    const path = `${environment.API_ENDPOINT}/diagram/${diagramId}/state/${state}/permission/edit`;

    return this.http.get(path);
  }

  public setUserData(user) {
    this.sentryService.setContext(user);
    this.intercom.bootIntercom(user);
    this.userSubject.next(user);
    this.sessionService.updateUser(user);
  }

  public getUserState() {
    if (!this.getToken()) {
      return 'unauthorized';
    }

    const user = this.userSubject.getValue();

    return user ? user.state : null;
  }

  public getToken() {
    return this.cookieSvc.get('auth-token') || this.authToken;
  }

  public setToken(token: string) {
    this.cookieSvc.set(
      'auth-token',
      token,
      null,
      '/',
      environment.DOMAIN,
      environment.SECURE_COOKIES
    );

    if (!this.getToken()) {
      this.authToken = token;
    }
  }

  public getTeamIndex() {
    const user = this.userSubject.getValue();

    if (user && user.userInTeam) {
      return user.userInTeam.teamIndex;
    }

    return null;
  }

  public getTeamDomain() {
    const user = this.userSubject.getValue();

    if (user && user.userInTeam) {
      return user.userInTeam.team.domain;
    }

    return '';
  }

  public userIsAdvisoryGroupMember() {
    const user = this.userSubject.getValue();

    if (user) {
      return user.advisoryGroupMember;
    }

    return false;
  }

  public updateUserData() {
    const path = `${environment.API_ENDPOINT}/account`;

    return this.http
      .get(path)
      .pipe(take(1))
      .subscribe(user => this.setUserData(user));
  }

  public getUserData() {
    return this.user$.toPromise(); // emulation of $q.deferred
  }

  public getUserDataSync() {
    return this.userSubject.getValue();
  }

  public getUserTeams() {
    const path = `${environment.API_ENDPOINT}/organizations`;

    return this.http.get(path, {
      params: new HttpParams()
        .set('sort', 'name ASC')
        .set('type', 'my')
        .set('fields', 'teamIndex')
        .set('teamFields', '_id plan name')
        .set('state', 'active')
        .set('planType', 'free pro')
    });
  }

  public fbLogOut() {
    this.angularFireAuth.signOut();
  }

  public getMapState() {
    return this.mapState;
  }
}
